import { useState } from 'react';

import { MDText } from 'i18n-react';

import { CopyIcon, ShareIcon, TickIcon } from '@motorway/mw-highway-code';
import { LoadingIndicator } from '@motorway/mw-highway-code/alpha';
import { INTENT } from '@motorway/mw-highway-code/enums';
import { useCopyToClipboard } from '@motorway/mw-highway-code/hooks';
import { Chip, toast } from '@motorway/mw-highway-code/pre-alpha';

import { INCODE_ANALYTICS } from '../../IncodeAnalytics';

import LocalTexts from './IncodeAuthorisationLink.texts.json';

import styles from './IncodeAuthorisationLink.module.scss';

const LocalT = new MDText(LocalTexts);

type IncodeAuthorisationLinkProps = {
  headingElement?: 'h1' | 'h2' | 'h3';
};

export const IncodeAuthorisationLink = ({
  headingElement: HeadingElement = 'h3',
}: IncodeAuthorisationLinkProps) => {
  const [loading] = useState(false);

  const { copy, isCopied } = useCopyToClipboard();

  // Replace this with the actual url once it's fetched
  const authUrl = 'https://CURRENT_URL/verify-your-identity?id=:enquiryId';

  const onCopy = () => {
    INCODE_ANALYTICS.INCODE_CTA_CLICK({
      button_label: LocalT.translate('copyLink') as string,
      name: 'incode_copy_auth_link_clicked',
    });

    copy(authUrl);
  };

  const shareData = {
    text: LocalT.translate('shareLinkDescription') as string,
    title: LocalT.translate('shareLinkTitle') as string,
    url: authUrl,
  };

  const canShare = globalThis?.navigator?.canShare?.(shareData);

  const onShare = () => {
    INCODE_ANALYTICS.INCODE_CTA_CLICK({
      button_label: LocalT.translate('shareLink') as string,
      name: 'incode_share_auth_link_clicked',
    });

    globalThis?.navigator?.share(shareData)?.catch((err) => {
      if (err?.name === 'AbortError') {
        return;
      }

      toast({
        content: LocalT.translate('pleaseCopy') as string,
        heading: LocalT.translate('unableToShare') as string,
        variant: INTENT.ERROR,
      });
    });
  };

  const chipAttributes = isCopied
    ? { icon: TickIcon, label: LocalT.translate('copiedLink') }
    : { icon: CopyIcon, label: LocalT.translate('copyLink') };

  return <div className={styles.authorisationLink}>
    <HeadingElement className={styles.title}>{LocalT.translate('shareLinkTitle')}</HeadingElement>
    <p data-thc-style-override className={styles.description}>{LocalT.translate('shareLinkDescription')}</p>
    <div
      aria-label={loading ? LocalT.translate('fetchingLink') as string : undefined}
      aria-live="polite"
      className={styles.shareContainer}>
      {loading
        ? <LoadingIndicator size="medium" />
        : <>
          <p data-thc-style-override className={styles.shareTitle}>{LocalT.translate('authorisationLink')}</p>
          <p data-thc-style-override className={styles.shareUrl}>{authUrl}</p>
          <div className={styles.buttonContainer}>
            <Chip reverse disabled={isCopied} {...chipAttributes} onClick={onCopy} />
            {canShare && <Chip reverse icon={ShareIcon} label={LocalT.translate('shareLink')} onClick={onShare} />}
          </div>
        </>
      }
    </div>
  </div>;
};
